import _ from 'underscore';
import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { RadioGroup } from '@bingads-webui-react/radio';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { GenreSelectionDoubleListBox } from './genre-selection-double-list-box';

const genreTargetAllOptionIds = {
  AllGenres: 'AllGenres',
  SelectSpecifics: 'SelectSpecifics',
};

const radioOptions = [
  genreTargetAllOptionIds.AllGenres,
  genreTargetAllOptionIds.SelectSpecifics,
];

const GenreTargets = ({
  i18n,
  genreTargets,
  genreOptions,
  updateValidation,
}) => {
  const activity = useContext(ActivityContext);
  const isAllSelected = genreTargets == null || genreTargets.length === 0;

  const [selectedOptionId, setSelectedOptionId] = useState(radioOptions[isAllSelected ? 0 : 1]);

  const optionToString = {
    [genreTargetAllOptionIds.AllGenres]: i18n.getString(_TL_('All genres')),
    [genreTargetAllOptionIds.SelectSpecifics]: i18n.getString(_TL_('Let me choose specific genres')),
  };

  const renderItem = useCallback(id => optionToString[id], [optionToString]);
  const getItemId = _.identity;

  const onGenreTargetsChanged = useCallback((selections) => {
    const selectionIds = _.map(selections, selection => selection.id);
    updateValidation([], { genreTargets: selectionIds });
    if (activity) {
      activity.signal(`onGenreTargetChange - ${JSON.stringify({ genreTargets: selectionIds })}`);
    }
  }, [updateValidation, activity]);

  const onRadioGroupChange = useCallback((id) => {
    if (id === genreTargetAllOptionIds.AllGenres) {
      // reset the selections.
      updateValidation([], { genreTargets: [] });
    }

    setSelectedOptionId(id);
  }, [updateValidation]);

  return (
    <React.Fragment>
      <div>
        { i18n.getString(_TL_('What genre do you want your ads to target?')) }
      </div>
      <RadioGroup
        items={radioOptions}
        renderItem={renderItem}
        getItemId={getItemId}
        onChange={onRadioGroupChange}
        checked={selectedOptionId}
      />
      {
        selectedOptionId === genreTargetAllOptionIds.SelectSpecifics &&
        <GenreSelectionDoubleListBox
          i18n={i18n}
          genreOptions={genreOptions}
          genreTargets={genreTargets}
          onSelectionChange={onGenreTargetsChanged}
        />
      }
    </React.Fragment>
  );
};

GenreTargets.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  genreTargets: PropTypes.arrayOf(PropTypes.number).isRequired,
  genreOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

const formatGenreTargets = ({ i18n, genreTargets, genreOptions }) => {
  if (genreTargets === null ||
    genreTargets === undefined ||
    genreTargets.length === 0 ||
    genreTargets.length === genreOptions.length) {
    return i18n.getString(_TL_('All genres'));
  }

  const genreTargetStr = genreTargets.length + i18n.getString(_TL_(' targets selected: ')) +
    _.chain(genreTargets)
      .sortBy()
      .take(10)
      .map(genreTargetId => _.findWhere(genreOptions, { id: genreTargetId }).name)
      .join(i18n.getString(_TL_(', ')))
      .value();

  if (genreTargets.length > 10) {
    return genreTargetStr + i18n.getString(_TL_('...'));
  }

  return genreTargetStr;
};

const ValidatedGenreTargets = withCustomValidation(GenreTargets);

export { ValidatedGenreTargets, formatGenreTargets };
